import {
  IconBook,
  IconUpload,
  IconTrash,
  IconFile,
  IconFileText,
  IconTable,
  IconCheck,
  IconRefresh,
  IconTrashX,
  IconSelectAll,
  IconBrain,
  IconPlus,
  IconX,
  IconAlertTriangle,
  IconInfoCircle,
  IconDatabase,
} from "@tabler/icons-react";
import "./knowledge-training.css";
import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Row,
  Badge,
  ProgressBar,
  Spinner,
  Form,
  Modal,
  Nav,
  Tab,
} from "react-bootstrap";
import {
  getKnowledgeBaseFiles,
  uploadKnowledgeBaseFile,
  bulkUploadKnowledgeBaseFiles,
  deleteKnowledgeBaseFile,
  bulkDeleteKnowledgeBaseFiles,
  createKnowledgeBaseFileFormData,
  createBulkKnowledgeBaseFileFormData,
  validateKnowledgeBaseFile,
  processAgentKnowledge,
  clearAgentRag,
} from "../../../services/AgentsServices";
import Swal from "sweetalert2";

const UnifiedKnowledgeTraining = ({ hubMode = false, agentId = null, agentContext = null }) => {
  // Estados principais
  const [activeView, setActiveView] = useState('knowledge'); // 'knowledge', 'training'
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [quotaInfo, setQuotaInfo] = useState({ current_count: 0, max_files: 10 });
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [isDeletingBulk, setIsDeletingBulk] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  
  // Estados para treinamento
  const [selectedTrainingFiles, setSelectedTrainingFiles] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [showTrainingModal, setShowTrainingModal] = useState(false);
  const [trainingResults, setTrainingResults] = useState(null);
  
  const [isClearing, setIsClearing] = useState(false);

  const fileInputRef = useRef(null);

  // Configurações baseadas na API DRF
  const maxFiles = quotaInfo.max_files || 3;
  const maxFileSize = 1 * 1024 * 1024; // 1MB
  
  // Memoizar allowedTypes para evitar re-renders desnecessários
  const allowedTypes = useMemo(() => ({
    'application/pdf': { icon: IconFileText, name: 'PDF', color: 'danger' },
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { icon: IconFile, name: 'DOCX', color: 'info' },
    'text/csv': { icon: IconTable, name: 'CSV', color: 'success' },
    'application/vnd.ms-excel': { icon: IconTable, name: 'XLS', color: 'success' },
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': { icon: IconTable, name: 'XLSX', color: 'success' }
  }), []);

  // Função para validar arquivo usando o service
  const validateFile = useCallback((file) => {
    const validation = validateKnowledgeBaseFile(file, {
      maxSize: maxFileSize,
      allowedTypes: Object.keys(allowedTypes)
    });
    
    return validation.errors;
  }, [allowedTypes, maxFileSize]);

  // Função para formatar tamanho do arquivo
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Função para mostrar erros detalhados
  const showDetailedError = useCallback((error, context = 'upload') => {
    console.error(`Erro no ${context}:`, error);
    
    let title = "Erro no Upload";
    let message = "Ocorreu um erro inesperado. Tente novamente.";
    let icon = "error";

    if (error.response) {
      const { status, data } = error.response;
      
      switch (status) {
        case 400:
          title = "Dados Inválidos";
          if (data.detail) {
            message = data.detail;
          } else if (data.errors && Array.isArray(data.errors)) {
            const errorMessages = data.errors.map(err => {
              const fileName = err.file || 'Arquivo desconhecido';
              let errorDetails = [];
              
              if (err.error) {
                try {
                  const parsedError = typeof err.error === 'string' 
                    ? JSON.parse(err.error.replace(/'/g, '"'))
                    : err.error;
                  
                  if (parsedError.file_type) {
                    errorDetails.push('tipo de arquivo inválido');
                  }
                  if (parsedError.file_size) {
                    errorDetails.push('tamanho do arquivo inválido');
                  }
                  if (parsedError.original_filename) {
                    errorDetails.push('nome do arquivo inválido');
                  }
                  
                  if (errorDetails.length === 0) {
                    errorDetails.push(err.error);
                  }
                } catch {
                  errorDetails.push(err.error);
                }
              }
              
              return `• ${fileName}: ${errorDetails.join(', ')}`;
            });
            
            message = errorMessages.join('\n');
          } else if (data.file) {
            message = Array.isArray(data.file) ? data.file.join('\n') : data.file;
          } else if (data.message) {
            message = data.message;
          } else {
            message = "Os dados enviados não são válidos. Verifique os arquivos selecionados.";
          }
          break;
        case 401:
          title = "Não Autorizado";
          message = "Sua sessão expirou. Faça login novamente.";
          break;
        case 413:
          title = "Arquivo Muito Grande";
          message = "Um ou mais arquivos excedem o tamanho máximo permitido (1MB).";
          break;
        case 415:
          title = "Tipo de Arquivo Não Suportado";
          message = "Um ou mais arquivos possuem formato não aceito. Use apenas PDF, DOCX, CSV, XLS ou XLSX.";
          break;
        case 500:
          title = "Erro Interno";
          message = "Erro interno do servidor. Tente novamente em alguns minutos.";
          break;
        default:
          title = `Erro ${status}`;
          message = data.detail || data.message || "Erro desconhecido no servidor.";
      }
    } else if (error.request) {
      title = "Erro de Conexão";
      message = "Não foi possível conectar ao servidor. Verifique sua conexão de internet.";
    } else {
      title = "Erro Local";
      message = error.message || "Erro desconhecido na aplicação.";
    }

    Swal.fire({
      icon,
      title,
      html: message.includes('•') ? `<div style="text-align: left; white-space: pre-line;">${message}</div>` : message,
      confirmButtonText: "Entendi",
      customClass: {
        popup: 'text-start'
      }
    });
  }, []);

  // Carregar arquivos da base de conhecimento
  const loadKnowledgeBaseFiles = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = {
        ordering: '-created_at'
      };
      
      // Adicionar agent_id se fornecido
      if (agentId) {
        params.agent_id = agentId;
      }
      
      const response = await getKnowledgeBaseFiles(params);


      const { results = [], count = 0, current_count = 0, max_files = 3, quota_info = {} } = response.data;
      
      const files = results.length > 0 ? results : (Array.isArray(response.data) ? response.data : []);
      setUploadedFiles(files);
      
      setQuotaInfo({
        current_count: current_count || count || files.length,
        max_files: max_files,
        ...quota_info
      });

    } catch (error) {
      console.error('Erro ao carregar arquivos:', error);
      showDetailedError(error, 'carregamento de arquivos');
      setUploadedFiles([]);
    } finally {
      setIsLoading(false);
    }
  }, [agentId]);

  // Handler para upload de arquivos com auto-seleção para treinamento
  const handleFileUpload = useCallback(async (event, autoSelectForTraining = true) => {
    const files = Array.from(event.target.files);
    
    if (files.length === 0) return;

    const currentCount = quotaInfo.current_count || uploadedFiles.length;
    if (currentCount + files.length > maxFiles) {
      Swal.fire({
        icon: "warning",
        title: "Limite de Arquivos Atingido",
        text: `Você pode carregar no máximo ${maxFiles} arquivos. Atualmente você tem ${currentCount} arquivos.`,
        confirmButtonText: "Entendi"
      });
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    const validFiles = [];
    const invalidFiles = [];

    files.forEach(file => {
      const errors = validateFile(file);
      if (errors.length === 0) {
        validFiles.push(file);
      } else {
        invalidFiles.push({ file, errors });
      }
    });

    if (invalidFiles.length > 0) {
      const errorList = invalidFiles.map(({ file, errors }) => 
        `• ${file.name}: ${errors.join(', ')}`
      ).join('\n');
      
      Swal.fire({
        icon: "warning",
        title: "Arquivos com Problemas",
        html: `<div style="text-align: left;">Os seguintes arquivos não puderam ser processados:<br><br><code style="white-space: pre-line;">${errorList}</code></div>`,
        confirmButtonText: "Entendi"
      });
    }

    if (validFiles.length === 0) {
      setIsUploading(false);
      return;
    }

    try {
      const uploadedFilesList = [];

      if (validFiles.length === 1) {
        const file = validFiles[0];
        setUploadProgress(30);

        const formData = createKnowledgeBaseFileFormData(file, { 
          agent_id: agentId 
        });
        
        setUploadProgress(50);
        const uploadParams = agentId ? { agent_id: agentId } : {};
        const response = await uploadKnowledgeBaseFile(formData, uploadParams);
        setUploadProgress(100);

        const apiResponse = response.data;
        const fileData = apiResponse.file || apiResponse;
        
        uploadedFilesList.push(fileData);

        if (apiResponse.quota_info) {
          setQuotaInfo(prev => ({ ...prev, ...apiResponse.quota_info }));
        }

        Swal.fire({
          icon: "success",
          title: "Arquivo Carregado!",
          text: apiResponse.message || "O arquivo foi enviado com sucesso e será processado em breve.",
          timer: 2000,
          showConfirmButton: false,
        });
      } else {
        setUploadProgress(20);
        
        const formData = createBulkKnowledgeBaseFileFormData(validFiles, { 
          agent_id: agentId 
        });
        
        const progressInterval = setInterval(() => {
          setUploadProgress(prev => Math.min(prev + 15, 85));
        }, 300);

        const uploadParams = agentId ? { agent_id: agentId } : {};
        const response = await bulkUploadKnowledgeBaseFiles(formData, uploadParams);
        
        clearInterval(progressInterval);
        setUploadProgress(100);

        const responseData = response.data || {};
        const { uploaded = [], total_uploaded = 0, total_errors = 0 } = responseData;

        if (uploaded.length > 0) {
          const processedFiles = uploaded.map(fileData => ({
            id: fileData.id,
            name: fileData.filename || fileData.name,
            size: fileData.size,
            type: fileData.content_type || fileData.type,
            uploadedAt: fileData.uploaded_at || new Date().toISOString(),
            url: fileData.url,
            status: fileData.status || 'uploaded'
          }));

          uploadedFilesList.push(...processedFiles);
        }

        if (total_errors > 0) {
          Swal.fire({
            icon: "warning",
            title: "Upload Parcialmente Concluído",
            html: `<div style="text-align: left;">
                     <p><strong>${total_uploaded} arquivos</strong> enviados com sucesso.</p>
                     <p><strong>${total_errors} arquivos</strong> com erro.</p>
                   </div>`,
            confirmButtonText: "Entendi"
          });
        } else {
          Swal.fire({
            icon: "success",
            title: `${total_uploaded} Arquivos Carregados!`,
            text: "Todos os arquivos foram enviados com sucesso e serão processados em breve.",
            timer: 3000,
            showConfirmButton: false,
          });
        }
      }

      // Recarregar lista de arquivos após upload
      await loadKnowledgeBaseFiles();

      // Auto-selecionar arquivos para treinamento se especificado
      if (autoSelectForTraining && uploadedFilesList.length > 0 && agentId) {
        const newFileIds = uploadedFilesList.map(f => f.id);
        setSelectedTrainingFiles(prev => [...prev, ...newFileIds]);
        
        // Mudar para aba de treinamento automaticamente
        setActiveView('training');
        
        // Mostrar toast informativo
        Swal.fire({
          icon: "info",
          title: "Arquivos selecionados para treinamento!",
          text: `${uploadedFilesList.length} arquivo(s) foram automaticamente selecionados para treinar o agente.`,
          timer: 3000,
          showConfirmButton: false,
          toast: true,
          position: 'top-end'
        });
      }

    } catch (error) {
      showDetailedError(error, 'upload');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }, [uploadedFiles, validateFile, agentId, maxFiles, quotaInfo, loadKnowledgeBaseFiles, showDetailedError]);

  // Handler para remover arquivo
  const handleRemoveFile = useCallback(async (fileId) => {
    const result = await Swal.fire({
      title: "Remover arquivo?",
      text: "Esta ação não poderá ser desfeita. O arquivo será removido da base de conhecimento.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, remover",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#dc3545",
    });

    if (!result.isConfirmed) return;

    try {
      const response = await deleteKnowledgeBaseFile(fileId);

      setUploadedFiles(prev => prev.filter(file => file.id !== fileId));
      setSelectedTrainingFiles(prev => prev.filter(id => id !== fileId));

      if (response.data && response.data.quota_info) {
        setQuotaInfo(prev => ({ ...prev, ...response.data.quota_info }));
      } else {
        setQuotaInfo(prev => ({ 
          ...prev, 
          current_count: Math.max(0, prev.current_count - 1) 
        }));
      }

      Swal.fire({
        icon: "success",
        title: "Arquivo removido!",
        text: response.data?.message || "O arquivo foi removido da base de conhecimento.",
        timer: 2000,
        showConfirmButton: false,
      });

      // Atualizar lista de arquivos
      await loadKnowledgeBaseFiles();

    } catch (error) {
      showDetailedError(error, 'remoção');
    }
  }, [showDetailedError, loadKnowledgeBaseFiles]);

  // Função para obter ícone e cor do arquivo
  const getFileInfo = (type) => {
    return allowedTypes[type] || { icon: IconFile, name: 'FILE', color: 'secondary' };
  };

  // Handler para refresh manual
  const handleRefresh = useCallback(async () => {
    await loadKnowledgeBaseFiles();
  }, [loadKnowledgeBaseFiles]);

  // Funções para seleção múltipla na base de conhecimento
  const toggleSelectionMode = useCallback(() => {
    setIsSelectionMode(!isSelectionMode);
    setSelectedFiles([]);
  }, [isSelectionMode]);

  const toggleFileSelection = useCallback((file) => {
    setSelectedFiles(prev => {
      const isSelected = prev.some(f => f.id === file.id);
      if (isSelected) {
        return prev.filter(f => f.id !== file.id);
      } else {
        return [...prev, file];
      }
    });
  }, []);

  const selectAllFiles = useCallback(() => {
    if (selectedFiles.length === uploadedFiles.length) {
      setSelectedFiles([]);
    } else {
      setSelectedFiles([...uploadedFiles]);
    }
  }, [selectedFiles.length, uploadedFiles]);

  // Handler para deletar múltiplos arquivos
  const handleBulkDelete = useCallback(async () => {
    if (selectedFiles.length === 0) return;

    const result = await Swal.fire({
      title: `Remover ${selectedFiles.length} arquivo(s)?`,
      text: "Esta ação não poderá ser desfeita. Os arquivos serão removidos da base de conhecimento.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sim, remover",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#dc3545",
    });

    if (!result.isConfirmed) return;

    try {
      setIsDeletingBulk(true);
      
      const fileIds = selectedFiles.map(file => file.id);
      const response = await bulkDeleteKnowledgeBaseFiles(fileIds);
      
      const apiResponse = response.data;
      
      const deletedIds = apiResponse.deleted_files?.map(f => f.id) || fileIds;
      setUploadedFiles(prev => prev.filter(file => !deletedIds.includes(file.id)));
      setSelectedTrainingFiles(prev => prev.filter(id => !deletedIds.includes(id)));
      
      setSelectedFiles([]);
      setIsSelectionMode(false);
      
      if (apiResponse.quota_info) {
        setQuotaInfo(prev => ({ ...prev, ...apiResponse.quota_info.quota }));
      } else {
        setQuotaInfo(prev => ({ 
          ...prev, 
          current_count: Math.max(0, prev.current_count - deletedIds.length) 
        }));
      }

      let messageContent = `${apiResponse.summary?.deleted_count || deletedIds.length} arquivo(s) removido(s) com sucesso`;
      
      if (apiResponse.summary?.not_found_count > 0) {
        messageContent += `\n\n⚠️ ${apiResponse.summary.not_found_count} arquivo(s) não encontrado(s)`;
      }

      Swal.fire({
        icon: "success",
        title: "Arquivos removidos!",
        text: apiResponse.message || messageContent,
        timer: 3000,
        showConfirmButton: false,
      });

      // Atualizar lista de arquivos
      await loadKnowledgeBaseFiles();

    } catch (error) {
      console.error('Erro no bulk delete:', error);
      showDetailedError(error, 'remoção em lote');
    } finally {
      setIsDeletingBulk(false);
    }
  }, [selectedFiles, showDetailedError, loadKnowledgeBaseFiles]);

  // Funções para treinamento
  const handleTrainingFileSelection = (fileId) => {
    setSelectedTrainingFiles((prev) => {
      if (prev.includes(fileId)) {
        return prev.filter((id) => id !== fileId);
      } else {
        return [...prev, fileId];
      }
    });
  };

  const handleSelectAllTraining = () => {
    if (selectedTrainingFiles.length === uploadedFiles.length) {
      setSelectedTrainingFiles([]);
    } else {
      setSelectedTrainingFiles(uploadedFiles.map((file) => file.id));
    }
  };

  // Handler para iniciar treinamento
  const handleStartTraining = async () => {
    if (selectedTrainingFiles.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "Nenhum arquivo selecionado",
        text: "Selecione pelo menos um arquivo para iniciar o treinamento.",
      });
      return;
    }

    if (!agentId) {
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "ID do agente não encontrado.",
      });
      return;
    }

    try {
      setIsProcessing(true);
      setProcessingProgress(0);

      const progressInterval = setInterval(() => {
        setProcessingProgress((prev) => Math.min(prev + 10, 90));
      }, 500);

      const response = await processAgentKnowledge({
        agent_id: agentId,
        file_ids: selectedTrainingFiles,
      });

      clearInterval(progressInterval);
      setProcessingProgress(100);

      const results = response.data;
      const statusInfo = results.statusInfo || {};

      // Armazenar resultados para exibir no modal
      setTrainingResults(results);

      // Fechar modal de treinamento e abrir modal de resultados
      setShowTrainingModal(false);

      // Tratar diferentes tipos de resultado baseado na análise do TrainingModal
      if (statusInfo.type === 'success') {
        // Sucesso completo
        handleTrainingSuccess(results);
        setShowResultsModal(true);
      } else if (statusInfo.type === 'partial_success') {
        // Sucesso parcial (warnings)
        handlePartialSuccess(results);
        setShowResultsModal(true);
      } else {
        // Outros casos
        handleTrainingError(results, false);
        setShowResultsModal(true);
      }

      // Limpar seleção após sucesso
      setSelectedTrainingFiles([]);

    } catch (error) {
      console.error("Erro ao iniciar treinamento:", error);
      
      // Verificar se o erro tem dados da API
      if (error.response?.data) {
        const errorData = error.response.data;
        const statusInfo = errorData.statusInfo || {};
        
        setTrainingResults(errorData);
        
        if (statusInfo.type === 'processing_failed') {
          handleTrainingError(errorData, false);
        } else if (statusInfo.type === 'not_found') {
          handleAgentNotFound();
        } else {
          handleUnknownError(error);
        }
        
        setShowResultsModal(true);
      } else {
        // Erro de rede ou outro erro sem resposta da API
        handleUnknownError(error);
      }
      
      // Fechar modal de treinamento em caso de erro
      setShowTrainingModal(false);
    } finally {
      setIsProcessing(false);
      setProcessingProgress(0);
    }
  };

  // Handler para sucesso completo
  const handleTrainingSuccess = (results) => {
    Swal.fire({
      icon: "success",
      title: "Treinamento Concluído!",
      html: `
        <p><strong>Agente:</strong> ${results.agent_name}</p>
        <p><strong>Arquivos processados:</strong> ${results.processed_files}</p>
        <p><strong>Fragmentos criados:</strong> ${results.total_chunks}</p>
        <p><strong>Embeddings gerados:</strong> ${results.successful_embeddings}</p>
      `,
      timer: 5000,
      showConfirmButton: true,
      confirmButtonText: "Ver Detalhes"
    }).then((result) => {
      if (result.isConfirmed) {
        setShowResultsModal(true);
      }
    });
  };

  // Handler para sucesso parcial
  const handlePartialSuccess = (results) => {
    Swal.fire({
      icon: "warning",
      title: "Treinamento Concluído com Avisos",
      html: `
        <p><strong>Agente:</strong> ${results.agent_name}</p>
        <p><strong>Arquivos processados:</strong> ${results.processed_files} de ${results.summary?.total_files_submitted || selectedTrainingFiles.length}</p>
        <p><strong>Avisos encontrados:</strong> ${results.warnings?.length || 0}</p>
        <p>Clique em "Ver Detalhes" para mais informações.</p>
      `,
      showConfirmButton: true,
      confirmButtonText: "Ver Detalhes"
    }).then((result) => {
      if (result.isConfirmed) {
        setShowResultsModal(true);
      }
    });
  };

  // Handler para erros de processamento
  const handleTrainingError = (results, closeModal = true) => {
    Swal.fire({
      icon: "error",
      title: "Falha no Treinamento",
      html: `
        <p><strong>Agente:</strong> ${results.agent_name || agentContext?.name}</p>
        <p><strong>Erros encontrados:</strong> ${results.errors?.length || 0}</p>
        <p><strong>Arquivos com erro:</strong> ${results.summary?.files_with_errors || 0}</p>
        <p>Verifique os detalhes para mais informações.</p>
      `,
      showConfirmButton: true,
      confirmButtonText: "Ver Detalhes",
      showCancelButton: !closeModal,
      cancelButtonText: "Tentar Novamente"
    }).then((result) => {
      if (result.isConfirmed) {
        setShowResultsModal(true);
      }
    });
  };

  // Handler para agente não encontrado
  const handleAgentNotFound = () => {
    Swal.fire({
      icon: "error",
      title: "Agente Não Encontrado",
      text: "O agente selecionado não foi encontrado. Verifique se ele ainda existe.",
      confirmButtonText: "OK"
    });
  };

  // Handler para erros desconhecidos
  const handleUnknownError = (error) => {
    const errorMessage = error.message || "Erro desconhecido ao processar treinamento";
    
    Swal.fire({
      icon: "error",
      title: "Erro Inesperado",
      text: errorMessage,
      confirmButtonText: "OK"
    });

    console.error("Erro inesperado no treinamento:", error);
  };

  // Handler para limpeza profunda do RAG
  const handleDeepClean = useCallback(async () => {
    if (!agentId) {
      Swal.fire({
        icon: "warning",
        title: "Agente não selecionado",
        text: "Selecione um agente antes de realizar a limpeza profunda.",
        confirmButtonText: "Entendi",
      });
      return;
    }

    const result = await Swal.fire({
      icon: "warning",
      title: "Limpeza Profunda",
      html: `
        <div style="text-align: left; font-size: 14px;">
          <p>Esta ação irá <strong>remover permanentemente</strong> todos os dados de treinamento RAG deste agente, incluindo:</p>
          <ul>
            <li>Todos os embeddings gerados</li>
            <li>Vetores armazenados no Redis</li>
            <li>Cache de resultados de busca</li>
          </ul>
          <p class="mt-2 mb-0"><strong>Os arquivos da base de conhecimento não serão removidos</strong>, apenas os dados processados. Você precisará retreinar o agente após esta operação.</p>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: "Sim, limpar tudo",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
    });

    if (!result.isConfirmed) return;

    try {
      setIsClearing(true);
      const response = await clearAgentRag(agentId);
      const data = response.data;

      const verified = data.verification?.cleanup_complete;
      const deletedChunks = data.cleanup?.deleted_chunks || 0;

      Swal.fire({
        icon: verified ? "success" : "warning",
        title: verified ? "Limpeza Concluída!" : "Limpeza Parcial",
        html: `
          <div style="text-align: left; font-size: 14px;">
            <p><strong>Agente:</strong> ${data.agent_name}</p>
            <p><strong>Embeddings removidos:</strong> ${deletedChunks}</p>
            <p><strong>Verificação:</strong> ${verified ? "Completa — nenhum resíduo encontrado" : "Resíduos detectados — tente novamente"}</p>
            ${data.cleanup?.deep_cleanup_applied ? `<p><strong>Limpeza extra aplicada:</strong> ${data.cleanup.deep_cleanup_vectors_deleted} vetores adicionais removidos</p>` : ""}
          </div>
        `,
        confirmButtonText: "OK",
        timer: verified ? 5000 : undefined,
      });
    } catch (error) {
      console.error("Erro na limpeza profunda:", error);
      Swal.fire({
        icon: "error",
        title: "Erro na Limpeza",
        text: error.response?.data?.error || "Não foi possível realizar a limpeza profunda. Tente novamente.",
        confirmButtonText: "Entendi",
      });
    } finally {
      setIsClearing(false);
    }
  }, [agentId]);

  // Detectar mudanças de viewport
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Carregar arquivos quando o componente montar
  useEffect(() => {
    loadKnowledgeBaseFiles();
  }, [loadKnowledgeBaseFiles]);

  // Renderização condicional baseada no modo e dispositivo
  const renderHeader = () => {
    if (isMobile) {
      return (
        <div className="ukt-mobile-header">
          <h4>Base de Conhecimento & Treinamento</h4>
          {agentContext && (
            <p>
              Gerencie documentos e treine o agente <strong>{agentContext.agent_name || agentContext.name}</strong>
            </p>
          )}
        </div>
      );
    }
    
    if (hubMode) {
      return (
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div className="d-flex align-items-center">
            <IconBook className="me-3 text-primary" size={24} />
            <div>
              <h4 className="mb-0">Base de Conhecimento & Treinamento</h4>
              {agentContext && (
                <p className="text-muted mb-0">
                  Gerencie documentos e treine o agente <strong>{agentContext.agent_name || agentContext.name}</strong>
                </p>
              )}
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center">
          <IconBook className="me-3 text-primary" size={32} />
          <div>
            <h2 className="mb-0">Base de Conhecimento & Treinamento</h2>
            <p className="text-muted mb-0">Gerencie documentos e treine seus agentes IA</p>
          </div>
        </div>
      </div>
    );
  };

  // Renderizar navegação mobile ou desktop
  const renderNavigation = () => {
    if (isMobile) {
      return (
        <div className="ukt-mobile-nav">
          <Nav className="d-flex">
            <Nav.Item className="flex-fill">
              <Nav.Link
                active={activeView === "knowledge"}
                onClick={() => setActiveView("knowledge")}
                className="position-relative"
              >
                <div className="ukt-nav-icon">
                  <IconBook size={20} />
                </div>
                <div className="ukt-nav-text">Base de Conhecimento</div>
                <Badge bg="info" className="badge">
                  {quotaInfo.current_count}/{quotaInfo.max_files}
                </Badge>
              </Nav.Link>
            </Nav.Item>
            <Nav.Item className="flex-fill">
              <Nav.Link
                active={activeView === "training"}
                onClick={() => setActiveView("training")}
                className="position-relative"
              >
                <div className="ukt-nav-icon">
                  <IconBrain size={20} />
                </div>
                <div className="ukt-nav-text">Treinamento</div>
                {selectedTrainingFiles.length > 0 && (
                  <Badge bg="primary" className="badge">
                    {selectedTrainingFiles.length}
                  </Badge>
                )}
              </Nav.Link>
            </Nav.Item>
          </Nav>
        </div>
      );
    }
    
    return (
      <Nav variant="pills" className="mb-4">
        <Nav.Item>
          <Nav.Link
            active={activeView === "knowledge"}
            onClick={() => setActiveView("knowledge")}
            className="d-flex align-items-center border rounded border-primary"
          >
            <IconBook size={16} className="me-2" />
            Base de Conhecimento
            <Badge bg="info" className="ms-2">
              {quotaInfo.current_count}/{quotaInfo.max_files}
            </Badge>
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link
            active={activeView === "training"}
            onClick={() => setActiveView("training")}
            className="d-flex align-items-center border rounded border-primary"
          >
            <IconBrain size={16} className="me-2" />
            Treinamento
            {selectedTrainingFiles.length > 0 && (
              <Badge bg="primary" className="ms-2">
                {selectedTrainingFiles.length} selecionados
              </Badge>
            )}
          </Nav.Link>
        </Nav.Item>
      </Nav>
    );
  };

  return (
    <div
      className={
        isMobile
          ? "ukt-mobile-container"
          : hubMode
          ? "p-4"
          : "container-fluid py-4"
      }
    >
      {renderHeader()}
      {renderNavigation()}

      <div className={isMobile ? "ukt-mobile-content" : ""}>
        <Tab.Container activeKey={activeView}>
          <Tab.Content>
            {/* Aba Base de Conhecimento */}
            <Tab.Pane eventKey="knowledge">
              <div className="d-flex align-items-center gap-2 mb-4 flex-wrap">
                <Badge bg="info">
                  {quotaInfo.current_count}/{quotaInfo.max_files} arquivos
                </Badge>
                {uploadedFiles.length > 0 && (
                  <>
                    <Button
                      variant={
                        isSelectionMode ? "warning" : "outline-secondary"
                      }
                      size="sm"
                      onClick={toggleSelectionMode}
                    >
                      <IconSelectAll size={16} className="me-1" />
                      {isSelectionMode ? "Cancelar" : "Selecionar"}
                    </Button>
                    {isSelectionMode && selectedFiles.length > 0 && (
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={handleBulkDelete}
                        disabled={isDeletingBulk}
                      >
                        <IconTrashX size={16} className="me-1" />
                        {isDeletingBulk
                          ? "Removendo..."
                          : `Remover ${selectedFiles.length}`}
                      </Button>
                    )}
                  </>
                )}
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={handleRefresh}
                  disabled={isLoading}
                >
                  <IconRefresh
                    size={16}
                    className={isLoading ? "spinner-border" : ""}
                  />
                </Button>
                {agentId && (
                  <Button
                    variant="outline-danger"
                    size="sm"
                    className="ms-auto"
                    onClick={handleDeepClean}
                    disabled={isClearing}
                  >
                    {isClearing ? (
                      <Spinner animation="border" size="sm" className="me-1" />
                    ) : (
                      <IconTrash size={16} className="me-1" />
                    )}
                    {isMobile ? "" : (isClearing ? "Limpando..." : "Limpeza Profunda")}
                  </Button>
                )}
              </div>

              <Alert variant="info" className="mb-4">
                <h6>📚 Biblioteca de Documentos</h6>
                <p className="mb-2">
                  Esta é sua biblioteca de documentos para treinamento do seu
                  agente de IA.
                </p>
                <ul className="mb-0">
                  <li>
                    <strong>Tipos aceitos:</strong> PDF, DOCX, CSV, XLS, XLSX
                  </li>
                  <li>
                    <strong>Limite:</strong> Máximo {maxFiles} arquivos de até
                    1MB cada
                  </li>
                  <li>
                    <strong>Fluxo integrado:</strong> Após carregar, os arquivos
                    podem ser selecionados para treinamento
                  </li>
                </ul>
              </Alert>

              {/* Loading state */}
              {isLoading && (
                <div
                  className={
                    isMobile ? "ukt-mobile-loading" : "text-center py-5"
                  }
                >
                  {isMobile ? (
                    <>
                      <Spinner className="ukt-mobile-loading-spinner" />
                      <div className="ukt-mobile-loading-text">
                        Carregando documentos...
                      </div>
                      <div className="ukt-mobile-loading-subtitle">
                        Buscando arquivos da sua base de conhecimento
                      </div>
                    </>
                  ) : (
                    <Card className="text-center py-5">
                      <Card.Body>
                        <Spinner className="mb-3" />
                        <h5>Carregando documentos...</h5>
                        <p className="text-muted mb-0">
                          Buscando arquivos da sua base de conhecimento
                        </p>
                      </Card.Body>
                    </Card>
                  )}
                </div>
              )}

              {/* Lista de arquivos carregados */}
              {!isLoading && uploadedFiles.length > 0 && (
                <Card>
                  <Card.Header className="bg-light">
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-2">
                      <div className="d-flex align-items-center gap-2">
                        <h5 className="mb-0">
                          📚 Documentos na Base de Conhecimento
                        </h5>
                        {isSelectionMode && (
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={selectAllFiles}
                          >
                            <IconSelectAll size={14} className="me-1" />
                            {selectedFiles.length === uploadedFiles.length
                              ? "Desmarcar todos"
                              : "Selecionar todos"}
                          </Button>
                        )}
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        {isSelectionMode && selectedFiles.length > 0 && (
                          <Badge bg="warning">
                            {selectedFiles.length} selecionados
                          </Badge>
                        )}
                        <Badge bg="primary">
                          {uploadedFiles.length} documentos
                        </Badge>
                      </div>
                    </div>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      {uploadedFiles.map((file) => {
                        const fileInfo = getFileInfo(
                          file.file_type || file.content_type || file.type
                        );
                        const FileIcon = fileInfo.icon;

                        const isSelected = selectedFiles.some(
                          (f) => f.id === file.id
                        );

                        return (
                          <Col lg={6} xl={4} key={file.id} className="mb-4">
                            <Card
                              className={`h-100 border ${
                                isSelectionMode && isSelected
                                  ? "border-primary bg-light"
                                  : ""
                              }`}
                            >
                              <Card.Body className="p-3">
                                <div className="d-flex align-items-start justify-content-between mb-3">
                                  <div className="d-flex align-items-start">
                                    <div
                                      className={`text-${fileInfo.color} me-3`}
                                    >
                                      <FileIcon size={32} />
                                    </div>
                                    {isSelectionMode && (
                                      <Form.Check
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={() =>
                                          toggleFileSelection(file)
                                        }
                                        className="me-3"
                                      />
                                    )}
                                  </div>
                                  {!isSelectionMode && (
                                    <Button
                                      variant="outline-danger"
                                      size="sm"
                                      onClick={() => handleRemoveFile(file.id)}
                                      title="Remover arquivo"
                                    >
                                      <IconTrash size={16} />
                                    </Button>
                                  )}
                                </div>

                                <div className="flex-grow-1">
                                  <h6
                                    className="mb-2 text-truncate"
                                    title={file.name || file.original_filename}
                                  >
                                    {file.name || file.original_filename}
                                  </h6>
                                  <div className="d-flex flex-wrap gap-1 mb-2">
                                    <Badge bg={fileInfo.color}>
                                      {fileInfo.name}
                                    </Badge>
                                    <Badge bg="light" text="dark">
                                      {formatFileSize(
                                        file.file_size || file.size
                                      )}
                                    </Badge>
                                    {file.is_processed !== false && (
                                      <Badge bg="success" text="white">
                                        <IconCheck size={12} className="me-1" />
                                        Indexado
                                      </Badge>
                                    )}
                                  </div>
                                  <small className="text-muted">
                                    Adicionado em{" "}
                                    {new Date(
                                      file.created_at ||
                                        file.uploadedAt ||
                                        new Date()
                                    ).toLocaleString("pt-BR")}
                                  </small>
                                </div>
                              </Card.Body>
                            </Card>
                          </Col>
                        );
                      })}
                    </Row>
                  </Card.Body>
                </Card>
              )}

              {/* Área de Upload - Movida para baixo da lista */}
              {!isLoading && (
                <Card className="my-4">
                  <Card.Header>
                    <h6 className="mb-0">📤 Carregar Novos Documentos</h6>
                  </Card.Header>
                  <Card.Body>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept=".pdf,.docx,.csv,.xls,.xlsx"
                      onChange={(e) =>
                        handleFileUpload(e, agentId ? true : false)
                      } // Auto-selecionar para treinamento se tiver agentId
                      style={{ display: "none" }}
                      disabled={
                        isUploading || quotaInfo.current_count >= maxFiles
                      }
                    />

                    <div className="text-center py-3">
                      <IconUpload size={40} className="text-muted mb-3" />

                      <h6>
                        {quotaInfo.current_count >= maxFiles
                          ? `Limite atingido (${maxFiles} arquivos)`
                          : "Adicionar Documentos"}
                      </h6>

                      <p className="text-muted mb-3">
                        {quotaInfo.current_count >= maxFiles
                          ? "Remova alguns arquivos para adicionar novos"
                          : agentId
                          ? "Arquivos carregados serão automaticamente selecionados para treinamento"
                          : "Expanda sua base de conhecimento com novos documentos"}
                      </p>

                      <Button
                        variant={
                          quotaInfo.current_count >= maxFiles
                            ? "secondary"
                            : "primary"
                        }
                        onClick={() => fileInputRef.current?.click()}
                        disabled={
                          isUploading || quotaInfo.current_count >= maxFiles
                        }
                      >
                        <IconUpload className="me-2" size={16} />
                        {isUploading ? "Carregando..." : "Selecionar Arquivos"}
                      </Button>

                      <div className="mt-2">
                        <small className="text-muted">
                          {quotaInfo.current_count}/{maxFiles} arquivos • Máximo
                          1MB por arquivo
                        </small>
                      </div>
                    </div>

                    {/* Barra de progresso durante upload */}
                    {isUploading && (
                      <div className="mt-3 pt-3 border-top">
                        <ProgressBar
                          now={uploadProgress}
                          label={`${Math.round(uploadProgress)}%`}
                          className="mb-2"
                          style={{ height: "6px" }}
                        />
                        <div className="text-center">
                          <Spinner size="sm" className="me-2" />
                          <small>Processando e indexando documentos...</small>
                        </div>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              )}

              {/* Estado vazio */}
              {!isLoading && uploadedFiles.length === 0 && (
                <Card className="text-center py-5">
                  <Card.Body>
                    <IconBook size={64} className="text-muted mb-4" />
                    <h4>Base de conhecimento vazia</h4>
                    <p className="text-muted mb-4">
                      Comece carregando documentos para criar uma base de
                      conhecimento rica
                      <br />
                      para seus agentes IA. Os documentos serão indexados e
                      ficarão disponíveis
                      <br />
                      para seleção durante a criação de novos agentes.
                    </p>
                    <Button
                      variant="primary"
                      size="lg"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploading}
                    >
                      <IconUpload className="me-2" />
                      Carregar Primeiro Documento
                    </Button>
                  </Card.Body>
                </Card>
              )}
            </Tab.Pane>

            {/* Aba Treinamento */}
            <Tab.Pane eventKey="training">
              {/* Informações sobre o treinamento */}
              <Alert variant="info" className="mb-4">
                <h6>🧠 Treinamento Inteligente</h6>
                <p className="mb-2">
                  Selecione os documentos da base de conhecimento que serão
                  utilizados para treinar o agente. O agente será capaz de
                  responder perguntas baseadas no conteúdo destes documentos.
                </p>
                <ul className="mb-0">
                  <li>
                    <strong>Processamento:</strong> Os documentos serão
                    analisados e indexados
                  </li>
                  <li>
                    <strong>Melhoria contínua:</strong> Você pode treinar
                    novamente a qualquer momento
                  </li>
                  <li>
                    <strong>Base de conhecimento:</strong> Use documentos de
                    qualidade para melhores resultados
                  </li>
                </ul>
              </Alert>

              {/* Ações e estatísticas */}
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div className="d-flex align-items-center gap-3">
                  <Badge bg="info">
                    {uploadedFiles.length} documento(s) disponível(is)
                  </Badge>
                  {selectedTrainingFiles.length > 0 && (
                    <Badge bg="primary">
                      {selectedTrainingFiles.length} selecionado(s)
                    </Badge>
                  )}
                </div>
                <div className="d-flex gap-2">
                  {uploadedFiles.length > 0 && (
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={handleSelectAllTraining}
                    >
                      {selectedTrainingFiles.length === uploadedFiles.length
                        ? "Desmarcar todos"
                        : "Selecionar todos"}
                    </Button>
                  )}
                  <Button
                    variant="primary"
                    onClick={() => setShowTrainingModal(true)}
                    disabled={selectedTrainingFiles.length === 0}
                  >
                    <IconBrain size={16} className="me-2" />
                    Treinar
                  </Button>
                </div>
              </div>

              {/* Lista de arquivos para treinamento */}
              {isLoading ? (
                <Card className="text-center py-5">
                  <Card.Body>
                    <Spinner className="mb-3" />
                    <h5>Carregando documentos...</h5>
                    <p className="text-muted mb-0">
                      Buscando arquivos da base de conhecimento
                    </p>
                  </Card.Body>
                </Card>
              ) : uploadedFiles.length === 0 ? (
                <Card className="text-center py-5">
                  <Card.Body>
                    <IconBook size={64} className="text-muted mb-4" />
                    <h4>Nenhum documento encontrado</h4>
                    <p className="text-muted mb-4">
                      Você precisa adicionar documentos à base de conhecimento
                      antes de treinar o agente.
                    </p>
                    <Button
                      variant="primary"
                      onClick={() => setActiveView("knowledge")}
                    >
                      <IconPlus className="me-2" />
                      Adicionar Documentos
                    </Button>
                  </Card.Body>
                </Card>
              ) : (
                <Card>
                  <Card.Header className="bg-light">
                    <h5 className="mb-0">
                      📚 Selecionar Documentos para Treinamento
                    </h5>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      {uploadedFiles.map((file) => {
                        const fileInfo = getFileInfo(
                          file.file_type || file.content_type || file.type
                        );
                        const FileIcon = fileInfo.icon;
                        const isSelected = selectedTrainingFiles.includes(
                          file.id
                        );

                        return (
                          <Col lg={6} xl={4} key={file.id} className="mb-4">
                            <Card
                              className={`h-100 border cursor-pointer ${
                                isSelected ? "border-primary bg-light" : ""
                              }`}
                              style={{ cursor: "pointer" }}
                              onClick={() =>
                                handleTrainingFileSelection(file.id)
                              }
                            >
                              <Card.Body className="p-3">
                                <div className="d-flex align-items-start justify-content-between mb-3">
                                  <div className="d-flex align-items-start">
                                    <div
                                      className={`text-${fileInfo.color} me-3`}
                                    >
                                      <FileIcon size={32} />
                                    </div>
                                    <Form.Check
                                      type="checkbox"
                                      checked={isSelected}
                                      onChange={() =>
                                        handleTrainingFileSelection(file.id)
                                      }
                                      onClick={(e) => e.stopPropagation()}
                                      className="me-3"
                                    />
                                  </div>
                                  {isSelected && (
                                    <IconCheck
                                      size={20}
                                      className="text-primary"
                                    />
                                  )}
                                </div>

                                <div className="flex-grow-1">
                                  <h6
                                    className="mb-2 text-truncate"
                                    title={file.name || file.original_filename}
                                  >
                                    {file.name || file.original_filename}
                                  </h6>
                                  <div className="d-flex flex-wrap gap-1 mb-2">
                                    <Badge bg={fileInfo.color}>
                                      {fileInfo.name}
                                    </Badge>
                                    <Badge bg="light" text="dark">
                                      {formatFileSize(
                                        file.file_size || file.size
                                      )}
                                    </Badge>
                                    {file.is_processed !== false && (
                                      <Badge bg="success" text="white">
                                        <IconCheck size={12} className="me-1" />
                                        Processado
                                      </Badge>
                                    )}
                                  </div>
                                  <small className="text-muted">
                                    Adicionado em{" "}
                                    {new Date(
                                      file.created_at ||
                                        file.uploadedAt ||
                                        new Date()
                                    ).toLocaleString("pt-BR")}
                                  </small>
                                </div>
                              </Card.Body>
                            </Card>
                          </Col>
                        );
                      })}
                    </Row>
                  </Card.Body>
                </Card>
              )}
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>

        {/* Modal de Treinamento */}
        <Modal
          show={showTrainingModal}
          onHide={() => setShowTrainingModal(false)}
          size="lg"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>
              <IconBrain className="me-2 text-primary" size={24} />
              Treinar Agente: {agentContext?.name}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Alert variant="info" className="mb-4">
              <h6>🧠 Treinamento Inteligente</h6>
              <p className="mb-2">
                O agente será treinado com os documentos selecionados,
                permitindo respostas mais precisas baseadas no conteúdo dos seus
                arquivos.
              </p>
              <ul className="mb-0">
                <li>
                  <strong>Processamento:</strong> Os documentos serão analisados
                  e indexados
                </li>
                <li>
                  <strong>IA Avançada:</strong> Criação de embeddings para busca
                  semântica
                </li>
                <li>
                  <strong>Melhoria contínua:</strong> Você pode treinar
                  novamente a qualquer momento
                </li>
              </ul>
            </Alert>

            {/* Lista de arquivos selecionados */}
            {selectedTrainingFiles.length > 0 && (
              <Card>
                <Card.Header>
                  <h6 className="mb-0">
                    📚 Documentos selecionados para treinamento
                  </h6>
                </Card.Header>
                <Card.Body>
                  <Row>
                    {uploadedFiles
                      .filter((file) => selectedTrainingFiles.includes(file.id))
                      .map((file) => {
                        const fileInfo = getFileInfo(
                          file.file_type || file.content_type || file.type
                        );
                        const FileIcon = fileInfo.icon;

                        return (
                          <Col lg={6} key={file.id} className="mb-3">
                            <Card className="h-100 border-success bg-light">
                              <Card.Body className="p-3">
                                <div className="d-flex align-items-start justify-content-between mb-2">
                                  <div
                                    className={`text-${fileInfo.color} me-3`}
                                  >
                                    <FileIcon size={24} />
                                  </div>
                                  <IconCheck
                                    size={16}
                                    className="text-success"
                                  />
                                </div>

                                <div className="flex-grow-1">
                                  <h6
                                    className="mb-2 text-truncate"
                                    title={file.name || file.original_filename}
                                  >
                                    {file.name || file.original_filename}
                                  </h6>
                                  <div className="d-flex flex-wrap gap-1 mb-2">
                                    <Badge bg={fileInfo.color}>
                                      {fileInfo.name}
                                    </Badge>
                                    <Badge bg="light" text="dark">
                                      {formatFileSize(
                                        file.file_size || file.size
                                      )}
                                    </Badge>
                                    <Badge bg="success" text="white">
                                      <IconCheck size={12} className="me-1" />
                                      Selecionado
                                    </Badge>
                                  </div>
                                  <small className="text-muted">
                                    Adicionado em{" "}
                                    {new Date(
                                      file.created_at ||
                                        file.uploadedAt ||
                                        new Date()
                                    ).toLocaleString("pt-BR")}
                                  </small>
                                </div>
                              </Card.Body>
                            </Card>
                          </Col>
                        );
                      })}
                  </Row>
                </Card.Body>
              </Card>
            )}

            {/* Progresso do treinamento quando ativo */}
            {isProcessing && (
              <Card className="mt-4">
                <Card.Body>
                  <div className="d-flex align-items-center gap-3">
                    <Spinner size="sm" />
                    <div className="flex-grow-1">
                      <h6 className="mb-1">Processando documentos...</h6>
                      <ProgressBar
                        now={processingProgress}
                        label={`${processingProgress}%`}
                        className="mb-2"
                      />
                      <small className="text-muted">
                        Analisando e indexando {selectedTrainingFiles.length}{" "}
                        documento(s) para o agente
                      </small>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            )}

            {/* Status da seleção */}
            {selectedTrainingFiles.length > 0 && !isProcessing && (
              <Alert variant="success" className="mt-4 mb-0">
                <div className="d-flex align-items-center">
                  <IconCheck className="me-3" size={20} />
                  <div>
                    <strong>
                      {selectedTrainingFiles.length} documento(s) selecionado(s)
                    </strong>
                    <br />
                    <small>
                      Estes documentos serão processados para o agente usar nas
                      respostas através da tecnologia RAG.
                    </small>
                  </div>
                </div>
              </Alert>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowTrainingModal(false)}
              disabled={isProcessing}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={handleStartTraining}
              disabled={selectedTrainingFiles.length === 0 || isProcessing}
            >
              {isProcessing ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Processando...
                </>
              ) : (
                <>
                  <IconBrain className="me-2" size={16} />
                  Iniciar Treinamento ({selectedTrainingFiles.length})
                </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Modal de Resultados Detalhados */}
        <Modal
          show={showResultsModal}
          onHide={() => setShowResultsModal(false)}
          size="xl"
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>
              <IconBrain className="me-2" size={24} />
              Resultado do Treinamento - {agentContext?.name}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ maxHeight: "70vh", overflowY: "auto" }}>
            {trainingResults && (
              <>
                {/* Status Geral */}
                <Alert
                  variant={
                    trainingResults.success ||
                    trainingResults.statusInfo?.type === "success"
                      ? "success"
                      : trainingResults.errors?.length > 0
                      ? "danger"
                      : "warning"
                  }
                  className="mb-4"
                >
                  <div className="d-flex align-items-center">
                    {trainingResults.success ||
                    trainingResults.statusInfo?.type === "success" ? (
                      <IconCheck className="me-2" size={20} />
                    ) : (
                      <IconX className="me-2" size={20} />
                    )}
                    <div className="flex-grow-1">
                      <strong>
                        {trainingResults.success ||
                        trainingResults.statusInfo?.type === "success"
                          ? "Treinamento Concluído com Sucesso!"
                          : trainingResults.errors?.length > 0
                          ? "Treinamento Concluído com Erros"
                          : "Treinamento Concluído com Avisos"}
                      </strong>
                      <br />
                      <small>
                        Processamento realizado em{" "}
                        {trainingResults.processing_time_seconds
                          ? `${trainingResults.processing_time_seconds.toFixed(
                              1
                            )}s`
                          : "N/A"}
                      </small>
                    </div>
                  </div>
                </Alert>

                {/* Resumo Estatístico */}
                <Card className="mb-4">
                  <Card.Header>
                    <h6 className="mb-0">
                      <IconInfoCircle className="me-2" size={16} />
                      Resumo do Processamento
                    </h6>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={3}>
                        <div className="text-center">
                          <h4 className="text-primary mb-1">
                            {trainingResults.processed_files || 0}
                          </h4>
                          <small className="text-muted">
                            Arquivos Processados
                          </small>
                        </div>
                      </Col>
                      <Col md={3}>
                        <div className="text-center">
                          <h4 className="text-info mb-1">
                            {trainingResults.total_chunks || 0}
                          </h4>
                          <small className="text-muted">
                            Fragmentos Criados
                          </small>
                        </div>
                      </Col>
                      <Col md={3}>
                        <div className="text-center">
                          <h4 className="text-success mb-1">
                            {trainingResults.successful_embeddings || 0}
                          </h4>
                          <small className="text-muted">
                            Embeddings Gerados
                          </small>
                        </div>
                      </Col>
                      <Col md={3}>
                        <div className="text-center">
                          <h4 className="text-warning mb-1">
                            {trainingResults.stored_vectors || 0}
                          </h4>
                          <small className="text-muted">
                            Vetores Armazenados
                          </small>
                        </div>
                      </Col>
                    </Row>

                    {/* Barra de Progresso */}
                    {trainingResults.summary && (
                      <div className="mt-4">
                        <div className="d-flex justify-content-between mb-2">
                          <small>Progresso do Processamento</small>
                          <small>
                            {trainingResults.summary
                              .files_successfully_processed || 0}{" "}
                            de{" "}
                            {trainingResults.summary.total_files_submitted ||
                              selectedTrainingFiles.length}{" "}
                            arquivos
                          </small>
                        </div>
                        <ProgressBar
                          variant={
                            trainingResults.success
                              ? "success"
                              : trainingResults.warnings?.length > 0 &&
                                !trainingResults.errors?.length
                              ? "warning"
                              : trainingResults.errors?.length > 0 &&
                                trainingResults.summary
                                  ?.files_successfully_processed > 0
                              ? "info"
                              : "danger"
                          }
                          now={
                            trainingResults.summary?.total_files_submitted > 0
                              ? (trainingResults.summary
                                  .files_successfully_processed /
                                  trainingResults.summary
                                    .total_files_submitted) *
                                100
                              : 0
                          }
                          style={{ height: "8px" }}
                        />
                      </div>
                    )}
                  </Card.Body>
                </Card>

                {/* Erros Gerais */}
                {trainingResults.errors &&
                  trainingResults.errors.length > 0 && (
                    <Alert variant="danger" className="mb-4">
                      <h6>
                        <IconX className="me-2" size={16} />
                        Erros do Processamento
                      </h6>
                      {trainingResults.errors.map((error, index) => (
                        <div key={index} className="mt-2">
                          <div className="d-flex align-items-start">
                            <IconAlertTriangle
                              className="me-2 mt-1 flex-shrink-0"
                              size={16}
                            />
                            <div>
                              <strong>Erro:</strong> {error.message || error}
                              {error.details && (
                                <div>
                                  <small className="text-muted">
                                    {error.details}
                                  </small>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </Alert>
                  )}

                {/* Avisos */}
                {trainingResults.warnings &&
                  trainingResults.warnings.length > 0 && (
                    <Alert variant="warning" className="mb-4">
                      <h6>
                        <IconAlertTriangle className="me-2" size={16} />
                        Avisos do Processamento
                      </h6>
                      {trainingResults.warnings.map((warning, index) => (
                        <div key={index} className="mt-2">
                          <div className="d-flex align-items-start">
                            <IconInfoCircle
                              className="me-2 mt-1 flex-shrink-0"
                              size={16}
                            />
                            <div>
                              <strong>Aviso:</strong>{" "}
                              {warning.message || warning}
                              {warning.details && (
                                <div>
                                  <small className="text-muted">
                                    {warning.details}
                                  </small>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </Alert>
                  )}

                {/* Detalhes dos Arquivos */}
                {trainingResults.files_processed &&
                  trainingResults.files_processed.length > 0 && (
                    <Card className="mb-4">
                      <Card.Header>
                        <h6 className="mb-0">
                          <IconFile className="me-2" size={16} />
                          Detalhes por Arquivo
                        </h6>
                      </Card.Header>
                      <Card.Body>
                        {trainingResults.files_processed.map((file, index) => (
                          <div key={index} className="border rounded p-3 mb-3">
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <div>
                                <h6 className="mb-1">{file.file_name}</h6>
                                <div>
                                  <Badge bg="secondary" className="me-2">
                                    {file.file_type?.toUpperCase()}
                                  </Badge>
                                  <Badge
                                    bg={file.success ? "success" : "danger"}
                                    className="me-2"
                                  >
                                    {file.success ? "Sucesso" : "Erro"}
                                  </Badge>
                                  <small className="text-muted">
                                    {file.file_size_bytes
                                      ? `${(
                                          file.file_size_bytes / 1024
                                        ).toFixed(1)} KB`
                                      : "N/A"}
                                  </small>
                                </div>
                              </div>
                              <div className="text-end">
                                <small className="text-muted">
                                  {file.chunks_created || 0} chunks •{" "}
                                  {file.embeddings_generated || 0} embeddings •{" "}
                                  {file.vectors_stored || 0} vetores
                                </small>
                              </div>
                            </div>

                            {/* Estágios de Processamento */}
                            <Row className="text-center mt-3">
                              <Col>
                                <div
                                  className={`text-${
                                    file.processing_stages?.text_extraction
                                      ? "success"
                                      : "muted"
                                  }`}
                                >
                                  <IconFile size={16} />
                                  <br />
                                  <small>Extração</small>
                                </div>
                              </Col>
                              <Col>
                                <div
                                  className={`text-${
                                    file.processing_stages?.text_chunking
                                      ? "success"
                                      : "muted"
                                  }`}
                                >
                                  <IconBrain size={16} />
                                  <br />
                                  <small>Fragmentação</small>
                                </div>
                              </Col>
                              <Col>
                                <div
                                  className={`text-${
                                    file.processing_stages?.embedding_generation
                                      ? "success"
                                      : "muted"
                                  }`}
                                >
                                  <IconBrain size={16} />
                                  <br />
                                  <small>IA</small>
                                </div>
                              </Col>
                              <Col>
                                <div
                                  className={`text-${
                                    file.processing_stages?.vector_storage
                                      ? "success"
                                      : "muted"
                                  }`}
                                >
                                  <IconDatabase size={16} />
                                  <br />
                                  <small>Armazenamento</small>
                                </div>
                              </Col>
                            </Row>
                          </div>
                        ))}
                      </Card.Body>
                    </Card>
                  )}

                {/* Mensagem Final */}
                <div className="text-center mt-4">
                  <p className="text-muted">
                    {trainingResults.success ||
                    trainingResults.statusInfo?.type === "success"
                      ? "O agente está pronto para responder perguntas baseadas nos documentos treinados."
                      : "Verifique os erros e avisos acima para melhorar o treinamento."}
                  </p>
                </div>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowResultsModal(false)}
            >
              Fechar
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </div>
  );
};

export default UnifiedKnowledgeTraining;